import cn from 'classnames';

import { Article, useCheckoutContext } from '@zooplus/checkout-state';
import { isBitiba } from '@zooplus/commons-lang';
import { FlatBox, H4, P2 } from '@zooplus/ui-components';

import FeedbackMessage from '../../common/notifications/feedback-message';

import { getAppConfig } from '@config/get-app-config';
import { useTranslation } from '@hooks/use-translations';
import useWindowDimensions from '@hooks/use-window-dimensions';
import { CHECKOUT_STATE_PROJECT_ID, MESSAGE_PLACEMENT } from '@utilities/constants';
import { isArrayNotEmpty } from '@utilities/utils';

import styles from './check-order-and-delivery-options.module.scss';
import CheckOrderDeliveryCollapsed from './check-order-delivery-collapsed/check-order-delivery-collapsed';
import CheckOrderItem from './check-order-item/check-order-item';
import CheckOrderItemCollapsed from './check-order-item-collapsed/check-order-item-collapsed';

type CheckOrderAndDeliveryOptionsProps = {
  isContainerActive?: boolean;
};

const getNumberOfArticlesToShow = (width: number, articles: Article[]) => {
  const numberOfArticlesToShow = width >= 1024 ? 5 : width >= 544 ? 4 : 3;
  const truncatedArticles = articles?.length - numberOfArticlesToShow;
  return numberOfArticlesToShow + Number(truncatedArticles === 1);
};

const Articles = ({ articles }: { articles: Article[] }) => (
  <>
    {isArrayNotEmpty(articles) &&
      articles.map((article: Article) => <CheckOrderItem article={article} key={article.id} />)}
  </>
);

const CollapsedArticles = ({
  articles,
  numberOfArticlesToShow,
}: {
  articles: Article[];
  numberOfArticlesToShow: number;
}) => {
  const truncatedArticles = articles?.length - numberOfArticlesToShow;

  return (
    <div className={styles.articlesCollapsedWrapper}>
      {isArrayNotEmpty(articles) &&
        articles
          .slice(0, numberOfArticlesToShow)
          .map((article: Article, index: number) => (
            <CheckOrderItemCollapsed
              {...article}
              key={article.id}
              index={index}
              articlesLength={articles?.length}
              numberOfArticlesToShow={numberOfArticlesToShow}
            />
          ))}
      {isArrayNotEmpty(articles) && truncatedArticles > 1 && (
        <span className={styles.truncatedArticles}>
          <P2 data-testid="truncated-articles">+{truncatedArticles}</P2>
        </span>
      )}
    </div>
  );
};

const CheckOrderAndDeliveryOptions = ({ isContainerActive }: CheckOrderAndDeliveryOptionsProps) => {
  const { checkoutState } = useCheckoutContext(CHECKOUT_STATE_PROJECT_ID);
  const { t } = useTranslation();
  const appConfig = getAppConfig();
  const articles = checkoutState?.cart?.articles;
  const { width } = useWindowDimensions();
  const numberOfArticlesToShow = getNumberOfArticlesToShow(width, articles as Article[]);
  const isDomainBitiba = isBitiba(appConfig.site.domain);

  return (
    <>
      <FlatBox
        className={cn(styles.checkOrderWrapper)}
        rounded
        variant="white"
        data-testid="check-order-and-delivery-options">
        <FeedbackMessage placement={MESSAGE_PLACEMENT.PREVIEW_CHECK_ORDER_HEADER} />
        <div className={styles.title}>
          <H4>{t('checkout.preview.yourProducts')}</H4>
        </div>
        {isDomainBitiba && <CheckOrderDeliveryCollapsed />}
        {!isContainerActive ? (
          <CollapsedArticles
            articles={articles as Article[]}
            numberOfArticlesToShow={numberOfArticlesToShow}
          />
        ) : (
          <Articles articles={articles as Article[]} />
        )}
        {!isDomainBitiba && <CheckOrderDeliveryCollapsed />}
      </FlatBox>
      {checkoutState?.stateAttributes?.showSubscriptionBox && (
        <div className={styles.autoshipmentWrapper}>autoshipment goes here</div>
      )}
    </>
  );
};

export default CheckOrderAndDeliveryOptions;
